import { DestroyRef, inject, Injectable, Injector } from '@angular/core';
import { ErrorMessageResponse } from '@qtek/core/websockets-core';

import { WsErrorRule, WS_ERROR_RULES } from './ws-error-rules';

/**
 * Holds WS error rules from two sources: app-wide rules provided once at
 * bootstrap (`WS_ERROR_RULES` / `provideWsErrorRules`), and feature-scoped
 * rules registered at runtime (`useWsErrorRules`) by whichever
 * component/store actually owns the behavior. Scoped rules are checked first.
 */
@Injectable({ providedIn: 'root' })
export class WsErrorRulesRegistry {
  private readonly staticRules =
    inject(WS_ERROR_RULES, { optional: true }) ?? [];
  private readonly scopedRules = new Set<WsErrorRule>();

  /** Adds `rules` until the returned teardown function runs. */
  add(...rules: WsErrorRule[]): () => void {
    rules.forEach(rule => this.scopedRules.add(rule));
    return () => rules.forEach(rule => this.scopedRules.delete(rule));
  }

  find(
    message: ErrorMessageResponse,
    injector: Injector
  ): WsErrorRule | undefined {
    for (const rule of this.scopedRules) {
      if (rule.match(message, injector)) {
        return rule;
      }
    }
    return this.staticRules.find(rule => rule.match(message, injector));
  }
}

/**
 * Registers `rules` for as long as the calling component or store is alive.
 * Call from an injection context (constructor, field initializer, or a
 * signalStore `withMethods` factory).
 */
export function useWsErrorRules(...rules: WsErrorRule[]): void {
  const remove = inject(WsErrorRulesRegistry).add(...rules);
  inject(DestroyRef).onDestroy(remove);
}
